import React from "react";
import { AlertTriangle, RefreshCw, Bug, Home, ExternalLink, ChevronDown } from "lucide-react";
import logger from "../../utils/UnifiedLogger";
import errorTracker from "../../utils/ErrorTracker";
import PropTypes from "prop-types";
import { withTranslation } from "react-i18next";



class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            hasError: false,
            error: null,
            errorInfo: null,
            eventId: null,
            isRetrying: false,
        };

        this.logger = logger;
    }

    sendErrorToServer(error, errorInfo, eventId) {
        return errorTracker.trackApiError({
            message: error.message,
            code: error.name,
            status: 0,
            statusText: "React Frontend Error",
            url: window.location.href,
            method: "REACT_COMPONENT",
            responseData: null,
            requestId: eventId,
            duration: null,
            attempts: 1,
            timeout: null,
            curl: "N/A",
            headers: null,
            requestData: errorInfo,
            component: this.props.componentName || "Unknown",
            stack: error.stack,
        });
    }

    static getDerivedStateFromError(err) {
        // console.log("ErrorBoundary caught an error:", err);
        return { hasError: true };
    }

    componentDidCatch(error, errorInfo) {
        // Log the React error through UnifiedLogger
        const logEntry = this.logger.critical(
            "ERROR_BOUNDARY",
            "React Error Boundary caught an error",
            {
                error: {
                    name: error.name,
                    message: error.message,
                    stack: error.stack,
                },
                errorInfo: {
                    componentStack: errorInfo.componentStack,
                },
                component: this.props.componentName || "Unknown",
                props: this.props.logProps ? this.sanitizeProps(this.props) : null,
                timestamp: new Date().toISOString(),
                url: window.location.href,
                userAgent: navigator.userAgent,
            }
        );

        this.setState({
            error,
            errorInfo,
            eventId: logEntry?.id || `error_${Date.now()}`,
        });

        // 2️⃣ Send to server using your function
        const evId = logEntry?.id || `error_${Date.now()}`
        this.sendErrorToServer(error, errorInfo, evId);

        // Additional logging for specific error types
        if (error.name === "ChunkLoadError") {
            this.logger.warning(
                "ERROR_BOUNDARY",
                "Chunk load error detected - possible deployment update",
                {
                    component: this.props.componentName,
                    errorName: error.name,
                    suggestion: "User should refresh page"
                }
            );
        } else if (error.message?.includes("Loading chunk")) {
            this.logger.warning(
                "ERROR_BOUNDARY",
                "Chunk loading failure - app update detected",
                {
                    component: this.props.componentName,
                    errorMessage: error.message,
                    suggestion: "Page refresh required"
                }
            );
        } else if (error.message?.includes("Network")) {
            this.logger.error(
                "ERROR_BOUNDARY",
                "Network-related error in component",
                {
                    component: this.props.componentName,
                    errorMessage: error.message,
                    networkStatus: navigator.onLine
                }
            );
        }

        // External error reporting (Sentry, etc.)
        if (window.Sentry) {
            window.Sentry.captureException(error, {
                contexts: {
                    react: {
                        componentStack: errorInfo.componentStack,
                    },
                },
                tags: {
                    component: this.props.componentName || "Unknown",
                    errorBoundary: true,
                },
            });
        }
    }

    sanitizeProps = (props) => {
        const sanitized = {};
        Object.keys(props).forEach((key) => {
            if (
                typeof props[key] !== "function" &&
                !key.toLowerCase().includes("password") &&
                !key.toLowerCase().includes("token") &&
                !key.toLowerCase().includes("secret") &&
                !key.toLowerCase().includes("api") &&
                !key.toLowerCase().includes("key")
            ) {
                sanitized[key] = props[key];
            }
        });
        return sanitized;
    };

    handleRetry = () => {
        this.setState({ isRetrying: true });

        this.logger.info("ERROR_BOUNDARY", "User attempted error recovery", {
            component: this.props.componentName,
            eventId: this.state.eventId,
            errorType: this.state.error?.name,
            retryAttempt: Date.now(),
        });

        // Add a small delay for better UX and to allow any cleanup
        setTimeout(() => {
            this.setState({
                hasError: false,
                error: null,
                errorInfo: null,
                eventId: null,
                isRetrying: false,
            });

            this.logger.info("ERROR_BOUNDARY", "Error boundary reset completed", {
                component: this.props.componentName,
            });
        }, 500);
    };

    handleReportIssue = () => {
        this.logger.info("report_issue", this.props.componentName || "ErrorBoundary", {
            errorEventId: this.state.eventId,
            errorType: this.state.error?.name,
            errorMessage: this.state.error?.message,
            timestamp: Date.now(),
        });

        // Export app logs for the issue report
        this.logger.exportLogs();

        this.logger.info("ERROR_BOUNDARY", "User reported issue and exported logs", {
            component: this.props.componentName,
            eventId: this.state.eventId,
        });
    };

    handleGoHome = () => {
        this.logger.info("navigate_home", this.props.componentName || "ErrorBoundary", {
            errorEventId: this.state.eventId,
            fromError: true,
            timestamp: Date.now(),
        });

        this.logger.info("ERROR_BOUNDARY", "User navigated to home from error", {
            component: this.props.componentName,
            eventId: this.state.eventId,
        });

        window.location.href = "/";
    };

    handleReload = () => {
        this.logger.info("page_reload", this.props.componentName || "ErrorBoundary", {
            errorEventId: this.state.eventId,
            fromError: true,
            timestamp: Date.now(),
        });

        this.logger.info("ERROR_BOUNDARY", "User triggered page reload from error", {
            component: this.props.componentName,
            eventId: this.state.eventId,
        });

        window.location.reload();
    };

    getErrorMessage = () => {
        const { error } = this.state;
        if (!error) return "An unexpected error occurred";

        // Customize error messages based on error type
        if (error.name === "ChunkLoadError") {
            return "Failed to load application resources. This might be due to a network issue or updated version.";
        }
        if (error.message?.includes("Loading chunk")) {
            return "Application update detected. Please refresh the page to load the latest version.";
        }
        if (error.message?.includes("Network")) {
            return "Network connection issue detected. Please check your internet connection.";
        }
        if (error.name === "TypeError") {
            return "A technical error occurred while processing your request.";
        }
        if (error.name === "ReferenceError") {
            return "A component reference error occurred. This might be a temporary issue.";
        }

        return "An unexpected error occurred while running the application.";
    };

    getErrorSuggestion = () => {
        const { error } = this.state;
        if (!error) return "Please try refreshing the page or contact support.";

        if (error.name === "ChunkLoadError" || error.message?.includes("Loading chunk")) {
            return "This usually happens after an app update. Refreshing the page should resolve it.";
        }
        if (error.message?.includes("Network")) {
            return "Check your internet connection and try again.";
        }

        return "If this continues, please report the issue with the error reference below.";
    };

    componentDidMount() {
        // Log when error boundary mounts successfully
        this.logger.debug("ERROR_BOUNDARY", "Error boundary mounted", {
            component: this.props.componentName || "Unknown",
        });
    }

    componentWillUnmount() {
        // Log when error boundary unmounts
        this.logger.debug("ERROR_BOUNDARY", "Error boundary unmounting", {
            component: this.props.componentName || "Unknown",
            hadError: this.state.hasError,
        });
    }

    render() {
        const { t } = this.props;
        if (this.state.hasError) {
            const errorMessage = this.getErrorMessage();
            const errorSuggestion = this.getErrorSuggestion();
            const isDevelopment = import.meta.env.DEV;

            return (
                <div className="min-h-screen bg-gradient-to-br from-red-50 via-orange-50 to-yellow-50 flex items-center justify-center p-4">
                    <div className="max-w-2xl w-full">
                        {/* Error Icon */}
                        <div className="text-center mb-8">
                            <div className="inline-flex items-center justify-center w-20 h-20 bg-red-100 rounded-full mb-4">
                                <AlertTriangle className="w-10 h-10 text-red-600" />
                            </div>
                            <h1 className="text-3xl font-bold text-gray-900 mb-2">
                                {t('errorBoundary.wentWrong')}
                            </h1>
                            <p className="text-lg text-gray-600 max-w-md mx-auto">
                                {errorMessage}
                            </p>
                            <p className="text-sm text-gray-500 max-w-md mx-auto mt-2">
                                {errorSuggestion}
                            </p>
                        </div>

                        {/* Error Details Card */}
                        <div className="bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden">
                            <div className="p-8">
                                {/* Main Error Info */}
                                <div className="mb-6">
                                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                                        {t('errorBoundary.whatHappened')}
                                    </h3>
                                    <p className="text-gray-600 leading-relaxed">
                                        {t('errorBoundary.sorryMessage')}
                                    </p>
                                    {this.props.componentName && (
                                        <p className="text-sm text-gray-500 mt-2">
                                            {t('errorBoundary.errorComponent')}<code className="bg-gray-100 px-1 rounded">{this.props.componentName}</code>
                                        </p>
                                    )}
                                </div>

                                {/* Action Buttons */}
                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
                                    <button
                                        onClick={this.handleRetry}
                                        disabled={this.state.isRetrying}
                                        className="inline-flex items-center justify-center px-4 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white font-medium rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                                    >
                                        {this.state.isRetrying ? (
                                            <>
                                                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                                                {t('errorBoundary.retrying')}
                                            </>
                                        ) : (
                                            <>
                                                <RefreshCw className="w-4 h-4 mr-2" />
                                                {t('errorBoundary.tryAgain')}
                                            </>
                                        )}
                                    </button>

                                    <button
                                        onClick={this.handleGoHome}
                                        className="inline-flex items-center justify-center px-4 py-3 bg-gray-600 hover:bg-gray-700 text-white font-medium rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                                    >
                                        <Home className="w-4 h-4 mr-2" />
                                        {t('errorBoundary.goHome')}
                                    </button>

                                    <button
                                        onClick={this.handleReload}
                                        className="inline-flex items-center justify-center px-4 py-3 bg-orange-600 hover:bg-orange-700 text-white font-medium rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2"
                                    >
                                        <ExternalLink className="w-4 h-4 mr-2" />
                                        {t('errorBoundary.reload')}
                                    </button>
                                </div>

                                {/* Report Issue Section */}
                                <div className="border-t border-gray-200 pt-6">
                                    <button
                                        onClick={this.handleReportIssue}
                                        className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-800 hover:bg-gray-50 rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                                    >
                                        <Bug className="w-4 h-4 mr-2" />
                                        {t('errorBoundary.reportIssue')}
                                    </button>

                                    {this.state.eventId && (
                                        <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                                            <p className="text-xs text-gray-500 mb-1">{t('errorBoundary.errorReference')}</p>
                                            <code className="text-xs font-mono text-gray-700 bg-white px-2 py-1 rounded border select-all">
                                                {this.state.eventId}
                                            </code>
                                            <p className="text-xs text-gray-400 mt-1">
                                                {t('errorBoundary.errorDescription')}
                                            </p>
                                        </div>
                                    )}
                                </div>

                                {/* Development Error Details */}
                                {isDevelopment && (
                                    <div className="border-t border-gray-200 pt-6 mt-6">
                                        <details className="group">
                                            <summary className="cursor-pointer text-sm font-medium text-gray-600 hover:text-gray-800 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 rounded">
                                                <span className="select-none">🔧 {t('errorBoundary.errorDetails')}</span>
                                                <ChevronDown
                                                    className="float-right h-4 w-4 group-open:rotate-180 transition-transform duration-200"
                                                />

                                            </summary>

                                            <div className="mt-4 space-y-4">
                                                <div>
                                                    <h4 className="text-sm font-medium text-gray-700 mb-2">{t('errorBoundary.errorType')}</h4>
                                                    <pre className="text-xs bg-red-50 border border-red-200 rounded p-3 overflow-x-auto text-red-800">
                                                        {this.state.error?.name}: {this.state.error?.message || "No error message available"}
                                                    </pre>
                                                </div>

                                                <div>
                                                    <h4 className="text-sm font-medium text-gray-700 mb-2">{t('errorBoundary.errorStack')}</h4>
                                                    <pre className="text-xs bg-gray-50 border border-gray-200 rounded p-3 overflow-x-auto text-gray-700">
                                                        {this.state.errorInfo?.componentStack || "No component stack available"}
                                                    </pre>
                                                </div>

                                                {this.state.error?.stack && (
                                                    <div>
                                                        <h4 className="text-sm font-medium text-gray-700 mb-2">{t('errorBoundary.errorStackTrace')}</h4>
                                                        <pre className="text-xs bg-gray-50 border border-gray-200 rounded p-3 overflow-x-auto text-gray-700 max-h-40">
                                                            {this.state.error.stack}
                                                        </pre>
                                                    </div>
                                                )}

                                                <div>
                                                    <h4 className="text-sm font-medium text-gray-700 mb-2">{t('errorBoundary.errorInfo')}</h4>
                                                    <pre className="text-xs bg-blue-50 border border-blue-200 rounded p-3 overflow-x-auto text-blue-800">
                                                        {t('errorBoundary.errorComponentName')} {this.props.componentName || "Unknown"}
                                                        {t('errorBoundary.errorEventId')} {this.state.eventId}
                                                        {t('errorBoundary.errorTimestamp')} {new Date().toISOString()}
                                                        {t('errorBoundary.errorUrl')} {window.location.href}
                                                    </pre>
                                                </div>
                                            </div>
                                        </details>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Help Text */}
                        {/* <div className="text-center mt-6">
                            <p className="text-sm text-gray-500">
                                If this problem persists, please contact our support team with the error reference above.
                            </p>
                        </div> */}
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}
ErrorBoundary.propTypes = {
    componentName: PropTypes.string,
    t: PropTypes.func.isRequired,
    logProps: PropTypes.bool,
    children: PropTypes.node,
};
// export default ErrorBoundary;
export default withTranslation()(ErrorBoundary);